import { ReactElement, memo, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { RichTreeViewPro } from '@mui/x-tree-view-pro'
import { countNewDescendants, getChangedAncestorIds, StructurePreviewNode } from '../../utils'
import PreviewTreeItem from '../PreviewTreeItem'
import { StructurePreviewTreeItem } from './types'

interface Props {
  nodes: StructurePreviewNode[]
  alreadyAddedTemplates: string[]
  onRemoveTemplate: (templateGuid: string) => void
}

/** Right pane of the add-template dialog: the structure as it looks after saving */
const StructurePreview = ({
  nodes,
  alreadyAddedTemplates,
  onRemoveTemplate,
}: Props): ReactElement => {
  // Folders containing changes open by default until the user toggles them
  const [toggledExpansion, setToggledExpansion] = useState<Record<string, boolean>>({})

  const items = useMemo(() => {
    const added = new Set(alreadyAddedTemplates)
    const toItem = (node: StructurePreviewNode): StructurePreviewTreeItem => ({
      id: node.id,
      label: node.name,
      status: node.status,
      kind: node.kind,
      newDescendantCount: countNewDescendants(node),
      readded: !!node.templateGuid && added.has(node.templateGuid),
      onRemove: node.templateGuid ? () => onRemoveTemplate(node.templateGuid as string) : undefined,
      children: node.children.map(toItem),
    })
    return nodes.map(toItem)
  }, [nodes, alreadyAddedTemplates, onRemoveTemplate])

  const expandedItems = useMemo(() => {
    const changedAncestors = new Set(getChangedAncestorIds(nodes))
    const isExpanded = (id: string) => toggledExpansion[id] ?? changedAncestors.has(id)
    const collectExpanded = (list: StructurePreviewNode[]): string[] =>
      list.flatMap(node => [
        ...(isExpanded(node.id) ? [node.id] : []),
        ...collectExpanded(node.children),
      ])
    return collectExpanded(nodes)
  }, [nodes, toggledExpansion])

  if (nodes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
        Die Dokumentation ist noch leer.
      </Typography>
    )
  }

  return (
    <Box data-cy="template-structure-preview">
      <RichTreeViewPro
        items={items}
        disableSelection
        slots={{ item: PreviewTreeItem }}
        expandedItems={expandedItems}
        onItemExpansionToggle={(_, itemId, isExpanded) =>
          setToggledExpansion(toggled => ({ ...toggled, [itemId]: isExpanded }))
        }
      />
    </Box>
  )
}

// Memoized: the selection's urgent render keeps the previous preview without re-rendering it
export default memo(StructurePreview)
